// UserRankDetail.jsx
/**
 * UserRankDetail
 * --------------------------------------------------------------------
 * 목적:
 *  - 선택된 기간(page: daily/weekly/monthly)의 "단골 랭킹 테이블"을 표시.
 *  - 현재 기간 목록을 방문 수 기준으로 정렬하여 1위부터 나열.
 *  - 지난 기간(lastDetailValue)과 비교해 랭킹 증감(▲/▼/—) 또는 NEW 표시.
 *  - 각 행 "조회" 버튼 클릭 시 props.onView(element)로 상세 모달 오픈.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import RankTriangle from "../common/RankTriangle";

function UserRankDetail(props) {
  // 지난 기간 랭킹을 name → { rank, count }로 매핑한 객체
  const [lastRank, setLastRank] = useState({});
  // 현재 기간 정렬/필터에 사용할 배열 상태(불변성 유지를 위한 복사본 관리)
  const [rankArray, setRankArray] = useState([
    { name: "", count: 0, total_point: 0 },
  ]);

  // 테이블 공통 스타일
  const baseCell =
    "flex items-center justify-center border-b border-solid border-neutral-500";
  // 컬럼 정의(너비/라벨)
  const cols = [
    { w: "w-[10%]", label: props.t("userRankDetail.rank") }, // 순위
    { w: "w-[15%]", label: props.t("userRankDetail.diff") }, // 증감
    { w: "w-[15%]", label: props.t("userRankDetail.userName") }, // 이름
    { w: "w-[20%]", label: props.t("userRankDetail.visits") }, // 방문 수
    { w: "w-[20%]", label: props.t("userRankDetail.points") }, // 포인트 합계
    { w: "w-[20%]", label: props.t("userRankDetail.credit") }, // 조회 버튼
  ];

  // [1] 현재 기간 데이터 정렬: 방문 수 기준 내림차순
  useEffect(() => {
    if (props.rankDetailValue) {
      // 안전복사 후 정렬(원본 불변)
      const copy = [...(props.rankDetailValue[props.page] || [])];
      // ⚠️주의: count 필드 사용. API가 visit_count만 제공 시 아래 기준 변경 필요
      copy.sort((a, b) => b.count - a.count);
      setRankArray(copy);
    }
  }, [props.rankDetailValue, props.page]);

  // [2] 지난 기간 랭킹 정보를 빠르게 조회할 수 있도록 name → {rank, count} 맵 생성
  useEffect(() => {
    if (props.lastDetailValue) {
      const obj = {};
      (props.lastDetailValue[props.page] || []).forEach((el, idx) => {
        obj[el.name] = { rank: idx + 1, count: el.count };
      });
      setLastRank(obj);
    }
  }, [props.lastDetailValue, props.page]);

  // [3] 방문 수가 0이 아닌 항목만 출력(노이즈 제거)
  // ⚠️주의: 여기서도 count를 사용. API 필드 확인 필요
  const filtered = rankArray.filter((e) => e.count !== 0);

  return (
    <motion.div
      initial={{ opacity: 0 }} // 페이드 인 초기값
      animate={{ opacity: 1 }} // 페이드 인
      transition={{ duration: 0.5 }} // 전환 시간
      className="block w-[100%] px-[5%] py-10 border border-[#f3f4f6]"
      id="best-rank"
    >
      {/* 상단 타이틀/뒤로가기 */}
      <div className="flex items-center justify-center relative h-[50px] w-full">
        <div
          className="absolute left-2 text-[5vw] font-semibold cursor-pointer"
          onClick={props.goBack} // navigate(-1)로 브라우저 히스토리 뒤로가기
        >
          &lt;
        </div>
        <div className="text-[4.2vw] font-semibold text-center">
          {props.t(`userRankDetail.${props.page}`)}{" "}
          {props.t("userRankDetail.back")}
        </div>
      </div>

      {/* 테이블 헤더 */}
      <div className="mt-[2vh] mb-8">
        <div className="flex w-full h-10 text-[3.4vw] font-medium text-neutral-500">
          {cols.map(({ w, label }) => (
            <div key={label} className={`${w} ${baseCell}`}>
              {label}
            </div>
          ))}
        </div>

        {/* 테이블 바디 */}
        {filtered.length > 0 ? (
          filtered.map((element, index) => (
            <div
              className="best-rank__each"
              key={index} // ⚠️권장: 서버에서 고유 id 제공 시 key로 사용(인덱스는 재정렬 시 비권장)
              id={(index + 1) % 2 === 0 ? "even" : "odd"}
            >
              {/* 순위 표시 */}
              <div className="flex w-[10%] items-center justify-center text-[4vw] font-bold text-left">
                {index + 1}
              </div>

              {/* 증감: 지난 기간에 count=0이면 NEW, 그 외에는 삼각형 컴포넌트로 증감 표시 */}
              {lastRank[element.name]?.count === 0 ? (
                <div className="flex w-[15%] text-[3.5vw] text-orange-500 font-bold items-center justify-center">
                  NEW
                </div>
              ) : (
                <div className="flex w-[15%] text-[3.5vw] items-center justify-center">
                  {/* diff = (지난 순위) - (현재 순위). 음수면 상승(▲), 양수면 하락(▼)로 표현하는 컴포넌트 가정 */}
                  <RankTriangle
                    diff={lastRank[element.name]?.rank - (index + 1)}
                    unit=""
                  />
                </div>
              )}

              {/* 사용자 이름: 너무 길면 말줄임 */}
              <div className="flex w-[15%] items-center justify-center text-[3.8vw] font-medium text-left">
                {element.name.length > 4
                  ? `${element.name.slice(0, 3)}...`
                  : element.name}{" "}
              </div>

              {/* 방문 수: 표시엔 visit_count 사용(정렬/필터와의 필드 일치 여부 확인 필요) */}
              <div className="flex w-[20%] items-center justify-center text-[4vw] font-bold text-left">
                {element.visit_count}회
              </div>

              {/* 포인트 합계: 천단위 구분자 */}
              <div className="flex w-[20%] items-center justify-center text-[3vw] font-bold text-left">
                {element.total_point.toLocaleString()}p
              </div>

              {/* 상세 조회: 클릭 시 상위 컴포넌트에서 모달 오픈 */}
              <div
                className="flex w-[20%] items-center justify-center text-[3.5vw] font-medium text-left"
                onClick={() => props.onView(element)} // ⚠️버블링으로 아래 버튼 onClick과 중복 호출 가능
              >
                <button
                  className="flex rounded-xl w-[65%] text-center h-[30%] items-center justify-center text-white font-bold bg-violet-400"
                  onClick={() => props.onView(element)} // ⚠️중복(onClick 하나만 유지 권장)
                >
                  조회
                </button>
              </div>
            </div>
          ))
        ) : (
          // 빈 데이터 처리
          <div className="py-12 text-center text-neutral-500">
            데이터가 없습니다
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default UserRankDetail;
